"use client";

import { useEffect, useState } from "react";
import { Search, Loader as Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
function AnalogClock({
  offset
}: {
  offset: number;
}) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const utc = now.getTime() + now.getTimezoneOffset() * 60000;
      setTime(new Date(utc + 3600000 * offset));
    }, 1000);
    return () => clearInterval(timer);
  }, [offset]);
  const seconds = time.getSeconds();
  const minutes = time.getMinutes();
  const hours = time.getHours();
  const secDeg = seconds / 60 * 360;
  const minDeg = minutes / 60 * 360 + seconds / 60 * 6;
  const hourDeg = hours / 12 * 360 + minutes / 60 * 30;
  const formatNum = (num: number) => num.toString().padStart(2, '0');
  return <div className="flex flex-col items-center">
      <div className="relative h-16 w-16 rounded-lg border-2 border-border/50 bg-card/30">
        <div className="absolute left-1/2 top-1/2 z-10 h-1.5 w-1.5 rounded-lg bg-primary -translate-x-1/2 -translate-y-1/2" />
        
        <div className="absolute left-1/2 bottom-1/2 h-4 w-1 origin-bottom -translate-x-1/2 bg-foreground transition-transform duration-500 ease-out" style={{
        transform: `translateX(-50%) rotate(${hourDeg}deg)`
      }} />
        
        <div className="absolute left-1/2 bottom-1/2 h-6 w-1 origin-bottom -translate-x-1/2 bg-foreground/70 transition-transform duration-500 ease-out" style={{
        transform: `translateX(-50%) rotate(${minDeg}deg)`
      }} />
        
        <div className="absolute left-1/2 bottom-1/2 h-7 w-0.5 origin-bottom -translate-x-1/2 bg-primary transition-transform duration-100 linear" style={{
        transform: `translateX(-50%) rotate(${secDeg}deg)`
      }} />
      </div>
      
      <div className="mt-2 rounded-lg bg-white px-3 py-1 font-mono text-lg font-bold">
        <span className="text-black">{formatNum(hours)}:{formatNum(minutes)}</span>
        <span className="text-red-600 ml-1">{formatNum(seconds)}</span>
      </div>
    </div>;
}
function LaptopMockup() {
  return <div className="relative mx-auto w-full max-w-2xl perspective-1000 rotate-y-15 translate-x-12 translate-y-12">
      <div className="relative rounded-lg border border-border bg-card p-2 shadow-2xl drop-shadow-[0_0_15px_rgba(239,68,68,0.8)]">
        <div className="absolute top-4 left-4 z-20 font-mono text-[10px] font-bold tracking-widest text-primary/80">
          CRYPTO SCAM DATABASE
        </div>
        <div className="relative aspect-video overflow-hidden rounded-md bg-background border border-border/50">
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
            <div className="mb-4 h-1 w-12 bg-primary/50 rounded-lg" />
            <h3 className="font-mono text-xl font-bold tracking-tighter text-foreground md:text-2xl">
              Crypto Scam <br />
              <span className="text-primary">Database</span>
            </h3>
            <div className="mt-6 grid grid-cols-3 gap-2 opacity-20">
              {[...Array(9)].map((_, i) => <div key={i} className="h-1 w-8 bg-foreground rounded-lg" />)}
            </div>
          </div>
          
          <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-purple-500/20 via-pink-500/10 to-transparent animate-[pulse-gradient_3.5s_ease-in-out_infinite]" />
          <div className="absolute inset-0 pointer-events-none bg-linear-to-b from-transparent via-primary/5 to-transparent animate-pulse" />
          <div className="absolute bottom-2 right-4 z-20 font-mono text-[8px] uppercase tracking-widest text-muted-foreground/40">
            © 2026 CRYPTO SCAM DATABASE
          </div>
        </div>
      </div>
      <div className="relative -mt-1 h-4 w-[105%] -translate-x-[2.5%] rounded-b-xl border-x border-b border-border bg-card shadow-lg" />
      <div className="mx-auto h-1.5 w-24 rounded-b-lg bg-muted-foreground/20" />
    </div>;
}
export default function Intelligence() {
  const [caseRef, setCaseRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const handleVerify = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setVerified(true);
    }, 10000);
  };
  const worldClocks = [{
    city: "NEW YORK",
    offset: -5
  }, {
    city: "LONDON",
    offset: 0
  }, {
    city: "DUBAI",
    offset: 4
  }, {
    city: "SYDNEY",
    offset: 11
  }, {
    city: "CAPE TOWN",
    offset: 2
  }];
  const complianceText = "This site is licensed by \"The United States Security and Exchange Commission (SEC)\" • ";
  return <section data-section-id="2838" id="intelligence-verification-portal" className="relative min-h-screen w-full overflow-hidden bg-linear-to-br from-indigo-900 via-purple-900 to-black text-foreground">
      <div className="absolute inset-0 z-0">
        <div className="h-full w-full bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1728123/img/blockchain_forensics_grid.webp')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to bottom, hsl(240 10% 3% / 0.6) 0%, hsl(240 10% 3% / 0.9) 100%)"
      }} />
      </div>

      <div className="pointer-events-none absolute inset-x-4 inset-y-4 z-10 border md:inset-x-8 md:inset-y-8" style={{
      borderColor: "hsl(240 5% 15%)"
    }} aria-hidden="true" />

      <div className="absolute left-8 top-8 z-20 flex flex-wrap gap-6 md:left-12 md:top-12 md:gap-8">
        {worldClocks.map((clock, i) => <div key={clock.city} className="flex flex-col gap-2" data-index={i}>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {clock.city}
            </span>
            <AnalogClock offset={clock.offset} />
          </div>)}
      </div>

      <div className="relative z-20 flex min-h-screen items-center px-8 md:px-16 lg:px-24">
        <div className="grid w-full gap-16 lg:grid-cols-2 lg:items-center">
          <div className="max-w-3xl">
            <h1 className="mt-6 font-default font-semibold leading-[1.1] tracking-tighter text-foreground" style={{
            fontSize: "clamp(2.5rem, 6vw, 4.5rem)"
          }}>
              Crypto Scam <br />
              <span className="text-primary">Database</span>
            </h1>

            <p className="mt-8 max-w-xl font-default text-lg text-muted-foreground">
              Global leader in digital asset investigation, cybersecurity, and forensic ledger analysis.
            </p>

            <div className="mt-12 flex flex-col gap-6">
              {!verified ? <div className="flex flex-col gap-4">
                  <input type="text" placeholder="ENTER CASE REFERENCE ID" value={caseRef} onChange={e => setCaseRef(e.target.value)} className="h-14 w-full max-w-md border border-border bg-card px-6 font-mono text-sm uppercase tracking-widest text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none rounded-lg" />
                  <Button onClick={handleVerify} disabled={loading} className="h-14 w-full max-w-md bg-primary px-8 font-default text-sm font-semibold uppercase tracking-widest text-primary-foreground hover:bg-primary/90 rounded-lg">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "VERIFY CASE"}
                  </Button>
                </div> : <div className="rounded-lg border border-primary/30 bg-primary/5 p-8">
                  <div className="apply-flash">
                    <p className="font-default text-lg text-foreground">
                      For security reasons, please contact <span className="text-blue-500 font-semibold">forensics@cryptoscamdatabase.com</span>
                    </p>
                  </div>
                </div>}
            </div>
          </div>

          <div className="hidden lg:block">
            <LaptopMockup />
            <div className="mt-24 w-full overflow-hidden border-t border-primary/50 bg-card/80 py-3 backdrop-blur-sm rounded-lg">
              <div className="flex whitespace-nowrap animate-[marquee_10s_linear_infinite]">
                <span className="font-mono text-xs font-bold tracking-widest text-foreground px-4">
                  {complianceText.repeat(8)}
                </span>
                <span className="font-mono text-xs font-bold tracking-widest text-foreground px-4">
                  {complianceText.repeat(8)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-6">
            <div className="h-32 w-32 animate-spin rounded-lg border-4 border-primary border-t-transparent" />
            <span className="font-mono text-sm uppercase tracking-widest text-primary">
              DECRYPTING LEDGER DATA...
            </span>
          </div>
        </div>}

      <style>{`
        @keyframes pulse-border {
          0%, 100% { border-color: hsl(240 5% 15%); }
          50% { border-color: hsl(210 100% 55%); }
        }
        @keyframes pulse-gradient {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .perspective-1000 {
          perspective: 1000px;
        }
        .rotate-y-15 {
          transform: rotateY(15deg);
        }
      `}</style>
    </section>;
}